// The list written back out as a command line. Every argument is one piece, so the wrapped form is the same command
// with the one separator changed — which is the whole of what the layout switch does.

import { bundledLetter, type Entry } from "./entries";
import { findLong } from "./options";
import { quoteCmd, quoteWord, type Shell } from "./shell";

// Two spaces, so the arguments line up under the `curl` that introduces them rather than against the margin.
const CONTINUATION: Record<Shell, string> = { bash: " \\\n  ", cmd: " ^\n  " };

const QUOTE: Record<Shell, Quote> = { bash: quoteWord, cmd: quoteCmd };

// Written for the shell it was read in, since a cmd command rewritten for bash is one its own shell cannot run.
export function writeCurl(entries: Entry[], wrapped: boolean, shell: Shell = "bash"): string {
  return ["curl", ...writeWords(entries, QUOTE[shell])].join(wrapped ? CONTINUATION[shell] : " ");
}

// Single-letter flags standing next to one another are written as the one bundle curl reads them as, `-sSL` rather
// than three words — which is how they are written by hand, and how they arrived if they arrived that way at all.
// The bundle is followed by its index rather than found by looking at the last word: a URL is allowed to be `-abc`,
// and a letter appended to that would be a letter added to somebody's address.
function writeWords(entries: Entry[], quote: Quote): string[] {
  const words: string[] = [];
  let bundle: number | null = null;

  for (const entry of entries) {
    const letter = bundledLetter(entry);

    if (letter === null) {
      words.push(writeEntry(entry, quote));
      bundle = null;
      continue;
    }

    if (bundle !== null) {
      words[bundle] += letter;
      continue;
    }

    bundle = words.length;
    words.push(`-${letter}`);
  }

  return words;
}

function writeEntry(entry: Entry, quote: Quote): string {
  if (entry.kind === "unknown") return entry.flag;

  if (entry.kind === "url") {
    // An empty row still writes `''`, as /url's empty parameter still writes its `=`: a row that put nothing into
    // the command could not be read back out of it, and the caret would leave the field it was typed in.
    const value = quote(entry.value, entry.variables);
    return entry.flag ? `${entry.flag} ${value}` : value;
  }

  const spec = findLong(entry.name);
  if (spec?.value === "none") return entry.flag;

  return `${entry.flag} ${quote(entry.value, entry.variables)}`;
}

type Quote = (value: string, variables?: readonly string[]) => string;
